import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { contentBriefPathes } from '../../../../../../../../constants/queryPathes';
import { statusCodes } from '../../../../../../../../constants/statusCodes';

import { axiosAPI } from '../../../../../../../../utils/axiosAPI';
import { isCurrentOutlineExists } from '../../../../../../../../utils/helpers/contentOptimizerHelpers';
import { getAxiosHeaders } from '../../../../../../../../utils/helpers/getAxiosHeaders';
import { openBasicErrorNotification } from '../../../../../../../../utils/helpers/openBasicErrorNotification';
import { useOrganizationSlug } from '../../../../../../../../utils/hooks/useOrganizationSlug';

import Loader from '../../../../../../../common/loader/Loader';

import { selectBriefIdOfCurrentContentOptimizer } from '../../../../store/contentOptimizerContent.selectors';

import OptimizerBriefForm from './OptimizerBriefForm';
import OptimizerBriefOutline from './OptimizerBriefOutline';

import './OptimizerBriefContent.scss';

const classes = {
    optimizerBriefTabContent: 'optimizer-brief-tab-content',
};

const OptimizerBriefContent = () => {
    const briefId = useSelector(selectBriefIdOfCurrentContentOptimizer);

    const organizationSlug = useOrganizationSlug();

    const [isBriefLoading, setIsBriefLoading] = useState(true);
    const [briefData, setBriefData] = useState(null);
    const [isContentInBrief, setIsContentInBrief] = useState(false);

    const [isOutlineLoading, setIsOutlineLoading] = useState(true);
    const [outlineData, setOutlineData] = useState(null);

    useEffect(() => {
        if (briefId && organizationSlug) {
            setIsBriefLoading(true);
            setIsOutlineLoading(true);

            axiosAPI
                .get(`${contentBriefPathes.getListOfContentBriefs}/${briefId}/versions`, {
                    ...getAxiosHeaders(),
                    params: {
                        slug: organizationSlug,
                        active: true,
                    },
                })
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        setBriefData(result?.data[0]);
                        setIsContentInBrief(checkBriefDataHasValues(result?.data[0].extras));
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    setIsBriefLoading(false);
                });

            axiosAPI
                .get(`${contentBriefPathes.getListOfContentBriefs}/${briefId}/outlines`, {
                    ...getAxiosHeaders(),
                    params: { slug: organizationSlug },
                })
                .then((result) => {
                    if (result?.status === statusCodes.success) {
                        const activeOutlineVersion = result.data?.find(
                            (outlineItem) => !!outlineItem.active
                        );

                        if (isCurrentOutlineExists(activeOutlineVersion?.outline)) {
                            setOutlineData(JSON.parse(activeOutlineVersion.outline));
                        }
                    }
                })
                .catch(() => {
                    openBasicErrorNotification();
                })
                .finally(() => {
                    setIsOutlineLoading(false);
                });
        }
    }, [briefId, organizationSlug]);

    const checkBriefDataHasValues = (briefExtras) => {
        if (briefExtras) {
            return Object.values(briefExtras).some((value) => {
                return value && value.length > 0;
            });
        }
    };

    return (
        <div className={classes.optimizerBriefTabContent}>
            {!briefId || isBriefLoading || isOutlineLoading ? (
                <Loader />
            ) : (
                <>
                    <OptimizerBriefForm
                        briefData={briefData}
                        isContentInBrief={isContentInBrief}
                        setBriefData={setBriefData}
                    />
                    {!!outlineData && <OptimizerBriefOutline outlineHtml={outlineData} showTitle />}
                </>
            )}
        </div>
    );
};

export default OptimizerBriefContent;
